import { useEffect, useMemo, useState } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import type { ApiResponse } from "@/services/api/types";
import {
  AnalyticsServiceV2,
  type SalesStats,
  type SessionMetrics,
  type ShopperFeedbackAverage,
} from "@/services/features/AnalyticsServiceV2";
import {
  type DateRange,
  storePerformanceService,
  type TrendComparisonWindow,
} from "@/services/StorePerformanceService";
import { getBrowserTimezone } from "@/utils/storeTime";

export type StorePerformanceMetric =
  | "sessions"
  | "unitsPerSession"
  | "missedRequests"
  | "responseTime"
  | "revenue"
  | "conversionRate"
  | "totalRequests"
  | "fulfilledRequests"
  | "cantFinds"
  | "cancelledRequests"
  | "dwellTime"
  | "namedShoppers"
  | "experienceRating";

export type StorePerformanceTrend =
  | { status: "loading" }
  | { status: "unavailable" }
  | {
      status: "ready";
      percent?: number;
      absoluteChange?: number;
      previousValue: number;
      direction: "up" | "down" | "flat";
      isGood: boolean;
    };

type TrendOptions = {
  enabled: boolean;
  storeIds: number[];
  comparisonWindow: TrendComparisonWindow;
  anchorComparisonToWeekdays?: boolean;
  currentRange: DateRange;
  deviceType: DeviceType;
  areaIds?: number[];
  latestSalesDate?: Date | null;
  metrics?: readonly StorePerformanceMetric[];
  currentValues: Partial<Record<StorePerformanceMetric, number | null | undefined>>;
};

export const storePerformanceTrendMetrics: StorePerformanceMetric[] = [
  "sessions",
  "unitsPerSession",
  "missedRequests",
  "responseTime",
  "revenue",
  "conversionRate",
  "totalRequests",
  "fulfilledRequests",
  "cantFinds",
  "cancelledRequests",
  "dwellTime",
  "namedShoppers",
  "experienceRating",
];

const lowerIsBetter = new Set<StorePerformanceMetric>([
  "missedRequests",
  "responseTime",
  "cantFinds",
  "cancelledRequests",
]);

type ComparisonValues = Record<StorePerformanceMetric, number | null | undefined>;
export type ComparisonProgress = { current: number; total: number };
type ComparisonProgressValues = Partial<Record<StorePerformanceMetric, ComparisonProgress>>;

type ComparisonResponse<T> = {
  data: T | undefined;
  succeeded: boolean;
};

const loadingComparisonValues = (): ComparisonValues =>
  Object.fromEntries(
    storePerformanceTrendMetrics.map((metric) => [metric, undefined]),
  ) as ComparisonValues;

const createTrend = (
  metric: StorePerformanceMetric,
  current: number | null | undefined,
  previous: number | null | undefined,
): StorePerformanceTrend => {
  if (current == null || previous === null) {
    return { status: "unavailable" };
  }

  if (previous === undefined) {
    return { status: "loading" };
  }

  if (current === previous) {
    return {
      status: "ready",
      percent: 0,
      previousValue: previous,
      direction: "flat",
      isGood: true,
    };
  }

  if (previous === 0) {
    const change = current;
    const direction = change > 0 ? "up" : "down";
    const isGood = lowerIsBetter.has(metric) ? change < 0 : change > 0;
    return {
      status: "ready",
      absoluteChange: change,
      previousValue: previous,
      direction,
      isGood,
    };
  }

  const change = ((current - previous) / Math.abs(previous)) * 100;
  const direction = change === 0 ? "flat" : change > 0 ? "up" : "down";
  const isGood = direction === "flat" || (lowerIsBetter.has(metric) ? change < 0 : change > 0);
  return {
    status: "ready",
    percent: Math.abs(change),
    previousValue: previous,
    direction,
    isGood,
  };
};

export const useStorePerformanceTrends = ({
  enabled,
  storeIds,
  comparisonWindow,
  anchorComparisonToWeekdays = true,
  currentRange,
  deviceType,
  areaIds,
  latestSalesDate,
  metrics = storePerformanceTrendMetrics,
  currentValues,
}: TrendOptions) => {
  const browserTimezone = getBrowserTimezone();
  const selectedStoreIdsKey = [...storeIds].sort((a, b) => a - b).join(",");
  const selectedStoreIds = useMemo(
    () => (selectedStoreIdsKey ? selectedStoreIdsKey.split(",").map(Number) : []),
    [selectedStoreIdsKey],
  );
  const areaIdsKey = areaIds?.join(",") ?? "";
  const requestedMetricsKey = metrics.join(",");
  const requestedMetrics = useMemo(
    () => new Set(requestedMetricsKey.split(",") as StorePerformanceMetric[]),
    [requestedMetricsKey],
  );
  const [comparisonValues, setComparisonValues] =
    useState<ComparisonValues>(loadingComparisonValues);
  const [comparisonProgress, setComparisonProgress] = useState<ComparisonProgressValues>({});

  const comparisonRange = useMemo(
    () =>
      storePerformanceService.getTrendComparisonRange(
        currentRange,
        comparisonWindow,
        browserTimezone,
        anchorComparisonToWeekdays,
      ),
    [anchorComparisonToWeekdays, browserTimezone, comparisonWindow, currentRange],
  );
  const salesComparisonWindow = useMemo(() => {
    const currentSalesWindow = storePerformanceService.getSalesQueryWindow(
      currentRange,
      latestSalesDate,
      browserTimezone,
    );

    return storePerformanceService.getAlignedSalesComparisonWindow(
      comparisonRange,
      currentSalesWindow,
      browserTimezone,
    );
  }, [browserTimezone, comparisonRange, currentRange, latestSalesDate]);
  const trends = useMemo(
    () =>
      Object.fromEntries(
        storePerformanceTrendMetrics.map((metric) => [
          metric,
          !requestedMetrics.has(metric) || !enabled || selectedStoreIds.length === 0
            ? ({ status: "unavailable" } satisfies StorePerformanceTrend)
            : createTrend(metric, currentValues[metric], comparisonValues[metric]),
        ]),
      ) as Record<StorePerformanceMetric, StorePerformanceTrend>,
    [comparisonValues, currentValues, enabled, requestedMetrics, selectedStoreIds.length],
  );

  useEffect(() => {
    let cancelled = false;

    if (!enabled || selectedStoreIds.length === 0) {
      setComparisonValues(loadingComparisonValues());
      setComparisonProgress({});
      return () => {
        cancelled = true;
      };
    }

    setComparisonValues(loadingComparisonValues());
    setComparisonProgress({});
    const analytics = AnalyticsServiceV2.getInstance();
    const baseParams = {
      storeIds: selectedStoreIds,
      startDate: storePerformanceService.formatDate(comparisonRange.start, browserTimezone),
      endDate: storePerformanceService.formatDate(comparisonRange.end, browserTimezone),
      ...(deviceType !== "all" ? { deviceType } : {}),
      ...(areaIdsKey ? { areaIds: areaIdsKey.split(",").map(Number) } : {}),
    };
    const sessionCount = (data: SessionMetrics | null | undefined) =>
      deviceType === "IN_AISLE"
        ? (data?.totalSessionsIncludingNoItems ?? 0)
        : (data?.totalSessions ?? 0);

    const fetchComparison = async <T>(
      metric: string,
      fetch: () => Promise<ApiResponse<T>>,
    ): Promise<ComparisonResponse<T>> => {
      try {
        return { data: (await fetch()).data, succeeded: true };
      } catch (error) {
        console.error(`Error fetching ${metric} comparison data:`, error);
        return { data: undefined, succeeded: false };
      }
    };

    const run = async () => {
      const empty = { data: undefined, succeeded: false };
      const needsSession = [
        "sessions",
        "unitsPerSession",
        "dwellTime",
        "namedShoppers",
        "conversionRate",
      ].some((metric) => requestedMetrics.has(metric as StorePerformanceMetric));
      const needsRequests = [
        "missedRequests",
        "responseTime",
        "totalRequests",
        "fulfilledRequests",
        "cantFinds",
        "cancelledRequests",
      ].some((metric) => requestedMetrics.has(metric as StorePerformanceMetric));
      const session = needsSession
        ? await fetchComparison("session", () => analytics.getSessionAnalytics(baseParams))
        : empty;
      const requests = needsRequests
        ? await fetchComparison("requests", () => analytics.getRequestAnalytics(baseParams))
        : empty;
      const missedRequests = requestedMetrics.has("missedRequests")
        ? await fetchComparison("missed requests", () =>
            analytics.getRequestAnalytics({ ...baseParams, requestStatus: "MISSED" }),
          )
        : empty;
      const completedRequests =
        requestedMetrics.has("responseTime") || requestedMetrics.has("fulfilledRequests")
          ? await fetchComparison("completed requests", () =>
              analytics.getRequestAnalytics({ ...baseParams, requestStatus: "COMPLETED" }),
            )
          : empty;
      const cantFindRequests = requestedMetrics.has("cantFinds")
        ? await fetchComparison("item-not-found requests", () =>
            analytics.getRequestAnalytics({ ...baseParams, requestStatus: "ITEM_NOT_FOUND" }),
          )
        : empty;
      const cancelledRequests = requestedMetrics.has("cancelledRequests")
        ? await fetchComparison("cancelled requests", () =>
            analytics.getRequestAnalytics({ ...baseParams, requestStatus: "CANCELED" }),
          )
        : empty;
      const salesParams = {
        ...baseParams,
        startDate: salesComparisonWindow.startDate,
        endDate: salesComparisonWindow.endDate,
        deviceType: deviceType !== "all" ? deviceType : null,
      };
      const revenueSalesParams = {
        ...salesParams,
        // Revenue is currently attributed exclusively to fitting room sessions.
        deviceType: "FITTING_ROOM",
      };
      const needsRevenue = requestedMetrics.has("revenue");
      const needsConversionRate = requestedMetrics.has("conversionRate");
      const salesSessions: ComparisonResponse<SessionMetrics> =
        needsConversionRate && salesComparisonWindow.isAvailable
          ? salesComparisonWindow.startDate === baseParams.startDate &&
            salesComparisonWindow.endDate === baseParams.endDate
            ? session
            : await fetchComparison("sales session", () =>
                analytics.getSessionAnalytics(salesParams),
              )
          : empty;
      const conversionSales =
        needsConversionRate && salesComparisonWindow.isAvailable
          ? await fetchComparison<SalesStats>("sales", () => analytics.getSalesSummary(salesParams))
          : empty;
      const revenueSales =
        needsRevenue && salesComparisonWindow.isAvailable
          ? await fetchComparison<SalesStats>("fitting room sales", () =>
              analytics.getSalesSummary(revenueSalesParams),
            )
          : empty;
      const feedback = requestedMetrics.has("experienceRating")
        ? await fetchComparison<ShopperFeedbackAverage>("shopper feedback", () =>
            analytics.getShopperFeedbackAverage({
              storeIds: selectedStoreIds,
              startDate: baseParams.startDate,
              endDate: baseParams.endDate,
            }),
          )
        : empty;

      const totalSessions = sessionCount(session.data);
      const totalRequests = requests.data?.totalRequests ?? 0;
      const requestPercentage = (count: number | undefined) =>
        totalRequests > 0 ? ((count ?? 0) / totalRequests) * 100 : null;
      const salesSessionCount = sessionCount(salesSessions.data);
      const convertedShoppers = conversionSales.data?.numberOfConvertedShoppers ?? 0;

      const values: ComparisonValues = {
        sessions: session.succeeded ? totalSessions || null : null,
        unitsPerSession:
          session.succeeded && totalSessions > 0
            ? (session.data?.totalItems ?? 0) / totalSessions
            : null,
        missedRequests:
          requests.succeeded && missedRequests.succeeded
            ? requestPercentage(missedRequests.data?.totalRequests)
            : null,
        responseTime:
          completedRequests.succeeded && (completedRequests.data?.totalRequests ?? 0) > 0
            ? (completedRequests.data?.totalTimeTakenSeconds ?? 0) /
              completedRequests.data!.totalRequests
            : null,
        revenue:
          revenueSales.succeeded && revenueSales.data?.totalBasketValue
            ? Number(revenueSales.data.totalBasketValue)
            : null,
        conversionRate:
          salesSessions.succeeded &&
          conversionSales.succeeded &&
          salesSessionCount > 0 &&
          convertedShoppers > 0
            ? (convertedShoppers / salesSessionCount) * 100
            : null,
        totalRequests: requests.succeeded ? totalRequests || null : null,
        fulfilledRequests:
          requests.succeeded && completedRequests.succeeded
            ? requestPercentage(completedRequests.data?.totalRequests)
            : null,
        cantFinds:
          requests.succeeded && cantFindRequests.succeeded
            ? requestPercentage(cantFindRequests.data?.totalRequests)
            : null,
        cancelledRequests:
          requests.succeeded && cancelledRequests.succeeded
            ? requestPercentage(cancelledRequests.data?.totalRequests)
            : null,
        dwellTime:
          session.succeeded && totalSessions > 0
            ? (deviceType === "IN_AISLE"
                ? (session.data?.totalDwellMillisecondIncludingNoItems ?? 0)
                : (session.data?.totalDwellMillisecond ?? 0)) /
              totalSessions /
              1000
            : null,
        namedShoppers:
          session.succeeded && totalSessions > 0
            ? ((session.data?.numberOfNamedShoppers ?? 0) / totalSessions) * 100
            : null,
        experienceRating:
          feedback.succeeded &&
          (feedback.data?.numberOfShopperRating ?? 0) > 0 &&
          (feedback.data?.totalShopperRating ?? 0) > 0
            ? feedback.data!.totalShopperRating / feedback.data!.numberOfShopperRating
            : null,
      };

      const requestProgress =
        requests.succeeded && totalRequests > 0
          ? {
              missedRequests: {
                current: missedRequests.data?.totalRequests ?? 0,
                total: totalRequests,
              },
              fulfilledRequests: {
                current: completedRequests.data?.totalRequests ?? 0,
                total: totalRequests,
              },
              cantFinds: {
                current: cantFindRequests.data?.totalRequests ?? 0,
                total: totalRequests,
              },
              cancelledRequests: {
                current: cancelledRequests.data?.totalRequests ?? 0,
                total: totalRequests,
              },
            }
          : {};
      const sessionProgress =
        session.succeeded && totalSessions > 0
          ? {
              namedShoppers: {
                current: session.data?.numberOfNamedShoppers ?? 0,
                total: totalSessions,
              },
            }
          : {};

      if (!cancelled) {
        setComparisonValues(values);
        setComparisonProgress({ ...requestProgress, ...sessionProgress });
      }
    };
    void run();

    return () => {
      cancelled = true;
    };
  }, [
    areaIdsKey,
    browserTimezone,
    comparisonRange,
    deviceType,
    enabled,
    salesComparisonWindow,
    requestedMetrics,
    selectedStoreIds,
  ]);

  return { comparisonRange, salesComparisonWindow, trends, comparisonProgress };
};
